import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDAWStore } from '../store/daw-store';
import { Users } from 'lucide-react';

/**
 * PresenceIndicators Component
 * 
 * Displays active users in the top toolbar with avatars and online status.
 * Implements Requirements 9.1, 9.2, 9.3, 9.4, 9.5
 * 
 * Features:
 * - Shows user avatars with colors
 * - Slide-in animation on join (Requirements 9.2)
 * - Fade-out animation on leave (Requirements 9.3)
 * - Hover tooltip with user info (Requirements 9.4)
 * - "+N more" overflow indicator (Requirements 9.5)
 */

interface PresenceIndicatorsProps {
  maxVisible?: number; // Maximum avatars to show before overflow (default: 5)
}

export function PresenceIndicators({ maxVisible = 5 }: PresenceIndicatorsProps) {
  const collaborativeCursors = useDAWStore((state) => state.collaborativeCursors);
  const currentUserId = useDAWStore((state) => state.currentUserId);
  const currentUserName = useDAWStore((state) => state.currentUserName);
  const currentUserColor = useDAWStore((state) => state.currentUserColor);
  const reduceMotion = useDAWStore((state) => state.reduceMotion);
  
  const [showOverflowDropdown, setShowOverflowDropdown] = useState(false);
  
  // Get all active users (excluding current user)
  const activeUsers = collaborativeCursors.filter((cursor) => cursor.userId !== currentUserId);
  
  // Add current user to the list
  const allUsers = [
    {
      userId: currentUserId,
      userName: currentUserName,
      color: currentUserColor,
      view: 'playlist' as const, // Current user's view
      lastUpdate: Date.now(),
    },
    ...activeUsers,
  ];
  
  // Split into visible and overflow users (Requirements 9.5)
  const visibleUsers = allUsers.slice(0, maxVisible);
  const overflowUsers = allUsers.slice(maxVisible);
  const overflowCount = overflowUsers.length;
  
  // Animation variants for slide-in/fade-out (Requirements 9.2, 9.3)
  const avatarVariants = {
    initial: {
      opacity: reduceMotion ? 1 : 0,
      x: reduceMotion ? 0 : 20,
      scale: reduceMotion ? 1 : 0.8,
    },
    animate: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: reduceMotion ? 0 : 0.3,
        ease: 'easeOut',
      },
    },
    exit: {
      opacity: 0,
      scale: reduceMotion ? 1 : 0.8,
      transition: {
        duration: reduceMotion ? 0 : 0.2,
        ease: 'easeIn',
      },
    },
  };
  
  // Get initials from user name
  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };
  
  // Format view name for display
  const formatView = (view: string) => {
    const viewMap: Record<string, string> = {
      'channel-rack': 'Channel Rack',
      'playlist': 'Playlist',
      'piano-roll': 'Piano Roll',
      'mixer': 'Mixer',
    };
    return viewMap[view] || view;
  };
  
  return (
    <div className="flex items-center gap-1 relative">
      {/* Visible user avatars */}
      <AnimatePresence mode="popLayout">
        {visibleUsers.map((user) => (
          <motion.div
            key={user.userId}
            variants={avatarVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="relative group"
          >
            {/* Avatar circle */}
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white cursor-pointer ring-2 ring-zinc-700 hover:ring-zinc-500 transition-all"
              style={{ backgroundColor: user.color }}
            >
              {getInitials(user.userName)}
            </div>
            
            {/* Hover tooltip (Requirements 9.4) */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
              <div className="bg-zinc-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap border border-zinc-700">
                <div className="font-semibold">{user.userName}</div>
                <div className="text-zinc-400 mt-0.5">
                  {formatView(user.view)}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-zinc-400">Online</span>
                </div>
                {/* Tooltip arrow */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                  <div className="w-2 h-2 bg-zinc-800 border-r border-b border-zinc-700 rotate-45"></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
      
      {/* Overflow indicator (Requirements 9.5) */}
      {overflowCount > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative"
        >
          <button
            onClick={() => setShowOverflowDropdown(!showOverflowDropdown)}
            className="w-8 h-8 rounded-full bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center text-xs font-semibold text-white cursor-pointer ring-2 ring-zinc-700 hover:ring-zinc-500 transition-all"
          >
            +{overflowCount}
          </button>
          
          {/* Overflow dropdown */}
          <AnimatePresence>
            {showOverflowDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute top-full right-0 mt-2 bg-zinc-800 rounded-lg shadow-xl border border-zinc-700 overflow-hidden z-50 min-w-[200px]"
              >
                {/* Header */}
                <div className="px-3 py-2 bg-zinc-900 border-b border-zinc-700 flex items-center gap-2">
                  <Users size={14} className="text-zinc-400" />
                  <span className="text-xs font-semibold text-zinc-300">
                    All Users ({allUsers.length})
                  </span>
                </div>
                
                {/* User list */}
                <div className="max-h-64 overflow-y-auto">
                  {overflowUsers.map((user) => (
                    <div
                      key={user.userId}
                      className="px-3 py-2 hover:bg-zinc-700 transition-colors flex items-center gap-2"
                    >
                      {/* Avatar */}
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
                        style={{ backgroundColor: user.color }}
                      >
                        {getInitials(user.userName)}
                      </div>
                      
                      {/* User info */}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">
                          {user.userName}
                        </div>
                        <div className="text-xs text-zinc-400 truncate">
                          {formatView(user.view)}
                        </div>
                      </div>
                      
                      {/* Online indicator */}
                      <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
      
      {/* Click outside to close dropdown */}
      {showOverflowDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowOverflowDropdown(false)}
        />
      )}
    </div>
  );
}
